import { useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import FieldHint from '../common/FieldHint';
import { useT } from '../../hooks/useT';

const GOAL_TYPE_VALUES = ['car', 'wedding', 'travel', 'electronics', 'emergency', 'real_estate', 'education', 'custom'];

export default function GoalForm({ onSubmit, loading, error, initialValues, onUseSame }) {
  const t = useT();
  const [form, setForm] = useState(() => ({
    title:          initialValues?.title          ?? '',
    type:           initialValues?.type           ?? 'custom',
    description:    initialValues?.description    ?? '',
    target_amount:  initialValues?.target_amount  != null ? String(initialValues.target_amount)  : '',
    monthly_budget: initialValues?.monthly_budget != null ? String(initialValues.monthly_budget) : '',
    deadline:       initialValues?.deadline ? initialValues.deadline.slice(0, 10) : '',
    priority:       initialValues?.priority       ?? 1,
  }));

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({
      ...form,
      target_amount: form.target_amount ? parseFloat(form.target_amount) : null,
      monthly_budget: form.monthly_budget ? parseFloat(form.monthly_budget) : null,
      deadline: form.deadline || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {onUseSame && (
        <button
          type="button"
          onClick={onUseSame}
          className="w-full py-2.5 rounded-xl border border-dashed border-slate-600 text-slate-400 text-sm hover:border-brand-500 hover:text-brand-400 transition-colors"
        >
          {t('regen_modal.use_same')}
        </button>
      )}
      {/* Goal Type */}
      <div>
        <label className="label flex items-center">
          {t('goal.form.type')}
          <FieldHint text={t('goal.form.type.hint')} />
        </label>
        <div className="grid grid-cols-4 gap-2">
          {GOAL_TYPE_VALUES.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => set('type', v)}
              className={`p-2.5 rounded-xl text-xs font-medium text-center transition-colors border ${
                form.type === v
                  ? 'bg-brand-600/20 border-brand-600 text-brand-300'
                  : 'bg-surface-hover border-surface-border text-slate-400 hover:border-slate-500'
              }`}
            >
              {t(`goal.form.type.${v}`)}
            </button>
          ))}
        </div>
      </div>

      <Input
        label={t('goal.form.title_label')}
        hint={t('goal.form.title_label.hint')}
        placeholder={t('goal.form.title_placeholder')}
        value={form.title}
        onChange={(e) => set('title', e.target.value)}
        required
      />

      <Input
        label={t('goal.form.description')}
        hint={t('goal.form.description.hint')}
        placeholder={t('goal.form.description_placeholder')}
        value={form.description}
        onChange={(e) => set('description', e.target.value)}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t('goal.form.target_amount')}
          hint={t('goal.form.target_amount.hint')}
          type="number"
          placeholder={t('goal.form.target_placeholder')}
          min="0"
          step="0.01"
          value={form.target_amount}
          onChange={(e) => set('target_amount', e.target.value)}
        />
        <Input
          label={t('goal.form.monthly_budget')}
          hint={t('goal.form.monthly_budget.hint')}
          type="number"
          placeholder={t('goal.form.monthly_budget_placeholder')}
          min="0"
          step="0.01"
          value={form.monthly_budget}
          onChange={(e) => set('monthly_budget', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t('goal.form.deadline')}
          hint={t('goal.form.deadline.hint')}
          type="date"
          value={form.deadline}
          onChange={(e) => set('deadline', e.target.value)}
        />
      </div>

      <div>
        <label className="label flex items-center">
          {t('goal.form.priority')}
          <FieldHint text={t('goal.form.priority.hint')} />
        </label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => set('priority', p)}
              className={`w-10 h-10 rounded-xl text-sm font-semibold transition-colors border ${
                form.priority === p
                  ? 'bg-brand-600 border-brand-600 text-white'
                  : 'bg-surface-hover border-surface-border text-slate-400 hover:border-slate-500'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-800 rounded-xl text-red-400 text-sm">
          {error}
        </div>
      )}

      <Button type="submit" className="w-full" loading={loading}>
        {t('goal.form.submit')}
      </Button>
    </form>
  );
}
